import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LogOut, ClipboardList, FileText, ExternalLink } from "lucide-react";
import { Logo } from "@/components/marketing/Nav";
import { ReadinessBadge, SeverityBadge, DocStatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMessage, API_BASE, tokenParam } from "@/lib/api";
import { toast } from "sonner";

function ReviewerShell({ children, title, back }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-softcream">
      <header className="sticky top-0 z-40 border-b border-hborder bg-navy">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3"><Logo dark /><span className="rounded bg-white/10 px-3 py-1 text-xs font-medium text-white/80">Reviewer</span></div>
          <button onClick={() => { logout(); navigate("/"); }} className="text-white/80 hover:text-white" aria-label="Sign out" data-testid="reviewer-logout"><LogOut size={18} /></button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {back && <Link to={back.to} className="mb-2 inline-block text-sm text-tblue hover:underline">← {back.label}</Link>}
        <h1 className="mb-6 font-serif text-2xl tracking-tight text-heading md:text-3xl">{title}</h1>
        {children}
      </main>
    </div>
  );
}

export function ReviewerQueue() {
  const [cases, setCases] = useState(null);
  useEffect(() => { api.get("/reviews/queue").then((r) => setCases(r.data)).catch(() => setCases([])); }, []);
  return (
    <ReviewerShell title="Review queue">
      {cases === null ? <div className="h-40 animate-pulse rounded-md bg-white" /> : cases.length === 0 ? (
        <p className="rounded-md border border-dashed border-hborder bg-white p-10 text-center text-sm text-muted"><ClipboardList className="mx-auto mb-3 text-muted/50" size={36} />No cases are awaiting review.</p>
      ) : (
        <div className="space-y-3">
          {cases.map((c) => (
            <Link key={c.id} to={`/reviewer/cases/${c.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-hborder bg-white p-5 " data-testid={`review-queue-${c.id}`}>
              <div><p className="font-semibold text-heading">{c.applicant_name}</p><p className="text-sm text-muted">{c.corridor?.destination}</p></div>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${c.open_findings ? "font-semibold text-coral" : "text-muted"}`}>{c.open_findings} open finding(s)</span>
                <ReadinessBadge state={c.readiness_state} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </ReviewerShell>
  );
}

export function ReviewerCase() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [review, setReview] = useState(null);
  const [note, setNote] = useState("");
  const [summary, setSummary] = useState("");

  const load = useCallback(() => {
    api.get(`/cases/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
    api.get(`/reviews/${id}`).then((r) => setReview(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return <ReviewerShell title="Case"><p className="text-muted">Case unavailable.</p></ReviewerShell>;
  if (!data) return <ReviewerShell title="Case review"><div className="h-40 animate-pulse rounded-md bg-white" /></ReviewerShell>;

  const { case: c, corridor, checklist, findings, profile } = data;

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); } };
  const start = () => act(() => api.post(`/reviews/${id}/start`), "Review started");
  const addNote = () => { if (!note.trim()) return; act(async () => { await api.post(`/reviews/${id}/notes`, { text: note }); setNote(""); }, "Note added"); };
  const complete = (outcome) => act(() => api.post(`/reviews/${id}/complete`, { outcome, summary }), "Review completed");

  return (
    <ReviewerShell title={`${profile?.full_name} · ${corridor?.destination}`} back={{ to: "/reviewer", label: "Review queue" }}>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-md border border-hborder bg-white p-5">
            <div className="flex items-center justify-between"><h2 className="font-semibold text-heading">Applicant</h2><ReadinessBadge state={c.readiness_state} /></div>
            <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <Info label="Full name" value={profile?.full_name} />
              <Info label="Date of birth" value={profile?.date_of_birth} />
              <Info label="Passport" value={profile?.passport_number} />
              <Info label="Passport expiry" value={profile?.passport_expiry} />
            </div>
          </div>

          <div className="rounded-md border border-hborder bg-white p-5">
            <h2 className="font-semibold text-heading">Documents & requirements</h2>
            <div className="mt-3 space-y-2">
              {checklist.map((r) => (
                <div key={r.code} className="flex items-center justify-between border-b border-hborder/50 py-2 last:border-0">
                  <span className="text-sm text-navy">{r.title}{r.required && <span className="ml-1 text-xs text-coral">*</span>}</span>
                  <div className="flex items-center gap-2">
                    {r.documents.map((d) => (
                      <a key={d.id} href={`${API_BASE}/documents/${d.id}/file${tokenParam()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-tblue hover:underline" data-testid={`reviewer-doc-${d.id}`}><FileText size={12} /> view</a>
                    ))}
                    {r.satisfied ? <DocStatusBadge status="Reviewed" /> : <span className="text-xs text-muted">missing</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-hborder bg-white p-5">
            <h2 className="font-semibold text-heading">Findings</h2>
            <div className="mt-3 space-y-3">
              {findings.length === 0 ? <p className="text-sm text-muted">No findings recorded.</p> : findings.map((f) => (
                <div key={f.id} className="rounded-lg border border-hborder p-3">
                  <div className="flex items-center gap-2"><SeverityBadge severity={f.severity} />{f.status !== "open" && <span className="text-xs text-emerald-700">{f.status}</span>}</div>
                  <p className="mt-1.5 font-medium text-heading">{f.title}</p>
                  <p className="text-sm text-muted">{f.explanation}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-md border border-hborder bg-white p-5">
            <h2 className="font-semibold text-heading">Reviewer actions</h2>
            <p className="mt-1 text-xs text-muted">A review is a preparation-quality check, not a visa decision.</p>
            {c.review_state === "requested" && <button onClick={start} className="mt-3 w-full rounded bg-navy px-4 py-2.5 text-sm font-semibold text-white" data-testid="start-review-btn">Start review</button>}
            <label className="mt-4 block text-sm font-medium text-navy" htmlFor="note">Factual note</label>
            <textarea id="note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-lg border border-hborder px-3 py-2 text-sm" data-testid="review-note-input" />
            <button onClick={addNote} className="mt-2 w-full rounded border border-hborder px-4 py-2 text-sm font-medium text-navy" data-testid="add-note-btn">Add note</button>
            <label className="mt-4 block text-sm font-medium text-navy" htmlFor="summary">Outcome summary</label>
            <textarea id="summary" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1 w-full rounded-lg border border-hborder px-3 py-2 text-sm" data-testid="review-summary-input" />
            <div className="mt-3 space-y-2">
              <button onClick={() => complete("approved_preparation")} className="w-full rounded bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white" data-testid="approve-prep-btn">Approve preparation</button>
              <button onClick={() => complete("returned_for_correction")} className="w-full rounded bg-coral px-4 py-2.5 text-sm font-semibold text-white" data-testid="return-correction-btn">Return for correction</button>
            </div>
          </div>

          {review?.notes?.length > 0 && (
            <div className="rounded-md border border-hborder bg-white p-5">
              <h2 className="font-semibold text-heading">Review notes</h2>
              <div className="mt-3 space-y-3">
                {review.notes.map((n, i) => (
                  <div key={i} className="border-l-2 border-tblue pl-3"><p className="text-sm text-navy">{n.text}</p><p className="text-xs text-muted">{n.author_name} · {new Date(n.created_at).toLocaleString()}</p></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </ReviewerShell>
  );
}

function Info({ label, value }) {
  return <div><p className="text-xs uppercase tracking-wider text-muted">{label}</p><p className="text-navy">{value || "Not provided"}</p></div>;
}
